import { useMemo, useState } from "react";
import { CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";

/**
 * Hook de ordenamiento reutilizable para tablas.
 * - Números: ordena numéricamente (mayor/menor).
 * - Texto: orden alfabético (localeCompare es, numeric).
 * - Valores nulos/vacíos van siempre al final.
 */
export function useSort(items, initialKey = null, initialDir = "asc") {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return items;
    const arr = [...(items || [])];
    arr.sort((a, b) => {
      const va = a?.[sortKey];
      const vb = b?.[sortKey];
      const na = va === null || va === undefined || va === "";
      const nb = vb === null || vb === undefined || vb === "";
      if (na && nb) return 0;
      if (na) return 1; // nulos al final
      if (nb) return -1;
      let cmp;
      if (typeof va === "number" && typeof vb === "number") {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), "es", { numeric: true, sensitivity: "base" });
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [items, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggleSort };
}

/** Encabezado de columna clickeable con indicador de orden. */
export function SortTh({ label, sortKey, activeKey, dir, onSort, align = "left", className = "" }) {
  const active = activeKey === sortKey;
  const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return (
    <th
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
      className={`px-5 py-3 font-semibold cursor-pointer select-none transition-colors hover:text-[#0F172A] ${alignCls} ${className}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        {label}
        {active ? (
          dir === "asc"
            ? <CaretUp size={12} weight="bold" className="text-[#0F172A]" />
            : <CaretDown size={12} weight="bold" className="text-[#0F172A]" />
        ) : (
          <CaretUpDown size={12} weight="bold" className="text-[#CBD5E1]" />
        )}
      </span>
    </th>
  );
}
